import { useState, Fragment, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import { useLanguage } from '../../../context/LanguageContext';
import { useProductPageTranslations } from '../../../context/productPageTranslations';
import {
  fetchBrandsAsync,
  fetchCategoriesAsync,
  fetchProductsByFiltersAsync,
  selectAllProducts,
  // selectBrands,
  selectCategories,
  selectProductListStatus,
  selectTotalItems,
  setSelectedProduct,
} from '../productSlice';
import { Dialog, Menu, Transition } from '@headlessui/react';
import {
  XMarkIcon,
  // FireIcon,
  CheckCircleIcon,
  XCircleIcon,
  EyeIcon,
  LockClosedIcon,
  MagnifyingGlassIcon,
  AdjustmentsHorizontalIcon,
  Bars3Icon,
  ComputerDesktopIcon,
  DocumentTextIcon,
  ShieldCheckIcon,
  PlayCircleIcon,
  CubeIcon,
  GlobeAltIcon,
  PaintBrushIcon,
  MusicalNoteIcon,
  FilmIcon,
  SparklesIcon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';
import {
  ChevronDownIcon,
  // FunnelIcon,
  // MinusIcon,
  // PlusIcon,
  // Squares2X2Icon,
  StarIcon,
} from '@heroicons/react/20/solid';
import { Link } from 'react-router-dom';
import { ITEMS_PER_PAGE } from '../../../app/constants';
// import Pagination from '../../common/Pagination';
import { Grid } from 'react-loader-spinner';

// Category icons mapping
const categoryIcons = {
  'windows': ComputerDesktopIcon,
  'Windows': ComputerDesktopIcon,
  'microsoft office': DocumentTextIcon,
  'Microsoft Office': DocumentTextIcon,
  'office': DocumentTextIcon,
  'Office': DocumentTextIcon,
  'antivirus': ShieldCheckIcon,
  'Antivirus': ShieldCheckIcon,
  'subscriptions': PlayCircleIcon,
  'Subscriptions': PlayCircleIcon,
  'entertainment': FilmIcon,
  'Entertainment': FilmIcon,
  'adobe': PaintBrushIcon,
  'Adobe': PaintBrushIcon,
  'gaming': CubeIcon,
  'Gaming': CubeIcon,
  'spotify': MusicalNoteIcon,
  'Spotify': MusicalNoteIcon,
  'netflix': FilmIcon,
  'Netflix': FilmIcon,
  'ai': SparklesIcon,
  'AI': SparklesIcon,
  'vpn': GlobeAltIcon,
  'VPN': GlobeAltIcon,
};

const getCategoryIcon = (value, label) => {
  return categoryIcons[value] || categoryIcons[label] || CubeIcon;
};

const sortOptions = [
  { name: 'Best Rating', sort: 'rating', order: 'desc', current: false },
  { name: 'Price: Low to High', sort: 'discountPrice', order: 'asc', current: false },
  { name: 'Price: High to Low', sort: 'discountPrice', order: 'desc', current: false },
];

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

// Helper function to get multilingual text
function getMultilingualText(field, language = 'en') {
  if (!field) return '';
  if (typeof field === 'string') return field;
  if (typeof field === 'object') {
    const result = field[language] || field['en'] || field['fr'] || Object.values(field)[0] || '';
    return typeof result === 'string' ? result : '';
  }
  return '';
}

export default function ProductList() {
  const dispatch = useDispatch();
  const { language } = useLanguage();
  const t = useProductPageTranslations(language);
  const [searchParams, setSearchParams] = useSearchParams();
  const products = useSelector(selectAllProducts);
  // const brands = useSelector(selectBrands);
  const categories = useSelector(selectCategories);
  const totalItems = useSelector(selectTotalItems);
  const status = useSelector(selectProductListStatus);
  const filters = [
    {
      id: 'category',
      name: t.category,
      options: categories,
    },
  ];

  // Initialize filter from URL if present
  const [filter, setFilter] = useState(() => {
    const categoryParam = searchParams.get('category');
    return categoryParam ? { category: [categoryParam] } : {};
  });

  const [sort, setSort] = useState({});
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [allStoreProducts, setAllStoreProducts] = useState([]);

  // Store full product catalog once loaded for zero-lag instant category switching
  useEffect(() => {
    if (Array.isArray(products) && products.length > 0 && (!filter.category || filter.category.length === 0) && !search) {
      setAllStoreProducts(products);
    }
  }, [products, filter.category, search]);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 250);
    return () => clearTimeout(timer);
  }, [search]);

  // Initial load for brands and categories
  useEffect(() => {
    dispatch(fetchBrandsAsync());
    dispatch(fetchCategoriesAsync());
  }, [dispatch]);

  // Sync category filter when URL changes (e.g., from navbar or browser navigation)
  useEffect(() => {
    const categoryParam = searchParams.get('category');
    if (categoryParam) {
      setFilter({ category: [categoryParam] });
    } else {
      setFilter((prev) => (prev.category && prev.category.length > 0 ? {} : prev));
    }
  }, [searchParams]);

  const handleFilter = (e, section, option) => {
    const newFilter = { ...filter };
    if (section.id === 'category') {
      const isCurrentlySelected = filter[section.id]?.includes(option.value);
      if (isCurrentlySelected) {
        newFilter[section.id] = [];
        setSearchParams({});
      } else {
        newFilter[section.id] = [option.value];
        setSearchParams({ category: option.value });
      }
    } else {
      if (e.target.checked) {
        if (newFilter[section.id]) {
          newFilter[section.id].push(option.value);
        } else {
          newFilter[section.id] = [option.value];
        }
      } else {
        const index = newFilter[section.id].findIndex(
          (el) => el === option.value
        );
        newFilter[section.id].splice(index, 1);
      }
    }
    setFilter(newFilter);
    setPage(1);
  };

  const handleSort = (e, option) => {
    const sort = { _sort: option.sort, _order: option.order };
    setSort(sort);
  };

  const handleSearch = (e) => {
    setSearch(e.target.value);
    setPage(1);
  };

  // Fetch products based on filter, sort, search, and page
  useEffect(() => {
    const pagination = { _page: page, _limit: ITEMS_PER_PAGE };
    const searchFilter = debouncedSearch ? { search: debouncedSearch } : {};
    dispatch(fetchProductsByFiltersAsync({ filter: { ...filter, ...searchFilter }, sort, pagination }));
  }, [dispatch, filter, sort, page, debouncedSearch]);

  useEffect(() => {
    setPage(1);
  }, [totalItems, sort]);

  // Instant client-side products resolution for ultra-fast UI response (<1ms)
  const displayProducts = useMemo(() => {
    const selectedCategory = filter.category?.[0];
    if (!selectedCategory) {
      return products;
    }
    const clean = (s) => (s || '').toLowerCase().replace(/[-_ ]+/g, '');
    const target = clean(selectedCategory);

    // If current Redux products already match this category, use them
    if (Array.isArray(products) && products.length > 0) {
      const matchesCategory = products.every((p) => {
        const c = clean(p.category);
        return c === target || c.includes(target) || target.includes(c);
      });
      if (matchesCategory) {
        return products;
      }
    }

    // Instantly filter from cached store products if available
    const sourcePool = allStoreProducts.length > 0 ? allStoreProducts : (Array.isArray(products) ? products : []);
    const instantFiltered = sourcePool.filter((p) => {
      const c = clean(p.category);
      return c === target || c.includes(target) || target.includes(c);
    });

    if (instantFiltered.length > 0) {
      return instantFiltered;
    }
    return products;
  }, [products, filter.category, allStoreProducts]);

  return (
    <div className="bg-gradient-to-b from-white via-white to-blue-50 min-h-screen">
      <div>
        <MobileFilter
          handleFilter={handleFilter}
          mobileFiltersOpen={mobileFiltersOpen}
          setMobileFiltersOpen={setMobileFiltersOpen}
          filters={filters}
          filter={filter}
          t={t}
        />

        <main className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          {/* Header Section */}
          <div className="py-8 sm:py-12 border-b border-blue-200/50">
            <div className="flex flex-col gap-6">
              <div className="flex items-center justify-between gap-4">
                <div className="flex-1">
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-neutral-900">
                    {filter.category && filter.category.length > 0
                      ? (categories.find(c => c.value?.toLowerCase() === filter.category[0]?.toLowerCase())?.label || filter.category[0].replace(/[-_]+/g, ' '))
                      : t.pageTitle}
                  </h1>
                  <p className="text-neutral-500 mt-2 text-sm sm:text-base">
                    {t.pageSubtitle}
                  </p>
                </div>

                {/* Controls */}
                <div className="flex items-center gap-2 sm:gap-3">
                  <button
                    type="button"
                    className="lg:hidden flex items-center gap-2 px-3 sm:px-4 py-2.5 bg-brand-500 hover:bg-brand-600 text-white rounded-xl font-medium transition-all duration-200 shadow-sm hover:shadow-md"
                    onClick={() => setMobileFiltersOpen(true)}
                  >
                    <AdjustmentsHorizontalIcon className="h-5 w-5" aria-hidden="true" />
                    <span className="text-sm hidden sm:inline">{t.filters}</span>
                  </button>

                  <Menu as="div" className="relative inline-block text-left">
                    <Menu.Button className="group inline-flex items-center justify-center gap-2 px-3 sm:px-4 py-2.5 bg-white border border-blue-200 rounded-xl text-xs sm:text-sm font-medium text-neutral-700 hover:bg-white hover:border-blue-300 transition-all duration-200 shadow-sm">
                      <span className="hidden sm:inline">{t.sortBy}</span>
                      <ChevronDownIcon
                        className="h-4 w-4 flex-shrink-0 text-neutral-400 group-hover:text-brand-500"
                        aria-hidden="true"
                      />
                    </Menu.Button>

                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-xl bg-white shadow-elegant ring-1 ring-blue-200 focus:outline-none">
                        <div className="py-1.5">
                          {sortOptions.map((option) => (
                            <Menu.Item key={option.name}>
                              {({ active }) => (
                                <p
                                  onClick={(e) => handleSort(e, option)}
                                  className={classNames(
                                    'block px-4 py-2.5 text-sm cursor-pointer transition-all duration-150',
                                    active ? 'bg-brand-50 text-brand-700 font-medium' : 'text-neutral-700 hover:text-neutral-900'
                                  )}
                                >
                                  {option.name}
                                </p>
                              )}
                            </Menu.Item>
                          ))}
                        </div>
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative w-full max-w-4xl">
                <MagnifyingGlassIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                <input
                  type="text"
                  placeholder={t.searchPlaceholder}
                  value={search}
                  onChange={handleSearch}
                  className="w-full pl-12 pr-4 py-3.5 bg-white border-2 border-blue-200 rounded-xl shadow-md placeholder-neutral-400 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/20 transition-all duration-200 text-base"
                />
                {search && (
                  <button
                    onClick={() => { setSearch(''); setPage(1); }}
                    className="absolute right-4 top-1/2 -translate-y-1/2 text-neutral-400 hover:text-neutral-600"
                  >
                    <XMarkIcon className="h-5 w-5" />
                  </button>
                )}
              </div>
            </div>
          </div>

          <section aria-labelledby="products-heading" className="pb-8 sm:pb-12">
            <h2 id="products-heading" className="sr-only">
              Products
            </h2>

            <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
              <DesktopFilter
                handleFilter={handleFilter}
                filters={filters}
                filter={filter}
                t={t}
              />
              <div className="lg:col-span-4">
                <ProductGrid products={displayProducts} status={status} search={search} language={language} t={t} />
              </div>
            </div>
          </section>


        </main>
      </div>
    </div>
  );
}

function MobileFilter({
  mobileFiltersOpen,
  setMobileFiltersOpen,
  handleFilter,
  filters,
  filter,
  t,
}) {
  return (
    <Transition.Root show={mobileFiltersOpen} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50 lg:hidden"
        onClose={setMobileFiltersOpen}
      >
        <Transition.Child
          as={Fragment}
          enter="transition-opacity ease-linear duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="transition-opacity ease-linear duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-neutral-900/40 backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 flex">
          <Transition.Child
            as={Fragment}
            enter="transition ease-in-out duration-300 transform"
            enterFrom="-translate-x-full"
            enterTo="translate-x-0"
            leave="transition ease-in-out duration-300 transform"
            leaveFrom="translate-x-0"
            leaveTo="-translate-x-full"
          >
            <Dialog.Panel className="relative mr-auto flex h-full w-full max-w-xs flex-col overflow-y-auto bg-white shadow-elegant">
              {/* Blue Header */}
              <div className="bg-blue-600 text-white px-4 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Bars3Icon className="w-5 h-5" />
                  <span className="font-semibold text-lg">Categories</span>
                </div>
                <button
                  type="button"
                  className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/20 hover:bg-white/30 text-white transition-all duration-200"
                  onClick={() => setMobileFiltersOpen(false)}
                >
                  <span className="sr-only">Close menu</span>
                  <XMarkIcon className="h-5 w-5" aria-hidden="true" />
                </button>
              </div>

              {/* Category List */}
              <div className="flex-1 overflow-y-auto">
                {filters.map((section) => (
                  <div key={section.id}>
                    {section.options.map((option) => {
                      const Icon = getCategoryIcon(option.value, option.label);
                      const isActive = filter[section.id]?.includes(option.value);
                      
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={(e) => {
                            handleFilter(e, section, option);
                            setMobileFiltersOpen(false);
                          }}
                          className={`
                            w-full flex items-center gap-3 px-4 py-4 text-sm transition-all duration-150
                            border-b border-gray-100 last:border-b-0
                            ${isActive 
                              ? 'bg-blue-50 text-blue-600 font-medium' 
                              : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                            }
                          `}
                        >
                          <Icon 
                            className={`w-5 h-5 flex-shrink-0 transition-colors ${
                              isActive ? 'text-blue-600' : 'text-gray-400'
                            }`} 
                          />
                          <span className="flex-1 text-left">{option.label}</span>
                          <ChevronRightIcon 
                            className={`w-4 h-4 flex-shrink-0 transition-all ${
                              isActive 
                                ? 'text-blue-600 translate-x-1' 
                                : 'text-gray-300'
                            }`} 
                          />
                        </button>
                      );
                    })}
                  </div>
                ))}
              </div>

              {/* Clear Filter Button */}
              {filter.category && filter.category.length > 0 && (
                <div className="border-t border-gray-200 p-4">
                  <button
                    type="button"
                    onClick={(e) => {
                      handleFilter(e, { id: 'category' }, { value: filter.category[0] });
                      setMobileFiltersOpen(false);
                    }}
                    className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 bg-red-50 hover:bg-red-100 rounded-lg transition-colors"
                  >
                    <XMarkIcon className="w-4 h-4" />
                    <span>Clear Filter</span>
                  </button>
                </div>
              )}
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition.Root>
  );
}

function DesktopFilter({ handleFilter, filters, filter, t }) {
  return (
    <form className="hidden lg:block sticky top-20 h-fit">
      {/* Category Sidebar with Blue Header */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-all duration-300">
        {/* Blue Header */}
        <div className="bg-blue-600 text-white px-4 py-3 flex items-center gap-3">
          <Bars3Icon className="w-5 h-5" />
          <span className="font-semibold">{t.filters}</span>
        </div>
        
        {filters.map((section) => (
          <div key={section.id}>
            {section.options.map((option, optionIdx) => {
              const Icon = getCategoryIcon(option.value, option.label);
              const isActive = filter[section.id]?.includes(option.value);
              
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={(e) => handleFilter(e, section, option)}
                  className={`
                    w-full flex items-center gap-3 px-4 py-3 text-sm transition-all duration-150
                    border-b border-gray-100 last:border-b-0
                    ${isActive 
                      ? 'bg-blue-50 text-blue-600 font-medium' 
                      : 'text-gray-700 hover:bg-blue-50 hover:text-blue-600'
                    }
                  `}
                >
                  <Icon 
                    className={`w-5 h-5 flex-shrink-0 transition-colors ${
                      isActive ? 'text-blue-600' : 'text-gray-400'
                    }`} 
                  />
                  <span className="flex-1 text-left">{option.label}</span>
                  <ChevronRightIcon 
                    className={`w-4 h-4 flex-shrink-0 transition-all ${
                      isActive 
                        ? 'text-blue-600 translate-x-1' 
                        : 'text-gray-300'
                    }`} 
                  />
                </button>
              );
            })}
          </div>
        ))}
        
        {/* Clear Filter Button */}
        {filter.category && filter.category.length > 0 && (
          <button
            type="button"
            onClick={(e) => handleFilter(e, { id: 'category' }, { value: filter.category[0] })}
            className="w-full flex items-center justify-center gap-2 px-4 py-3 text-sm font-medium text-red-600 hover:bg-red-50 transition-colors border-t border-gray-100"
          >
            <XMarkIcon className="w-4 h-4" />
            <span>Clear Filter</span>
          </button>
        )}
      </div>
    </form>
  );
}

function ProductGrid({ products, status, search, language, t }) {
  const dispatch = useDispatch();
  const isLoading = status === 'loading';
  const hasProducts = Array.isArray(products) && products.length > 0;

  return (
    <div>
      <div className="mx-auto">
        {/* Sleek top loading progress bar when switching categories */}
        {isLoading && (
          <div className="w-full mb-6">
            <div className="h-1.5 w-full bg-blue-100 overflow-hidden rounded-full">
              <div className="h-full bg-blue-600 rounded-full animate-pulse w-full" />
            </div>
          </div>
        )}

        {/* Empty search state */}
        {!hasProducts && search && !isLoading && (
          <div className="text-center py-16">
            <MagnifyingGlassIcon className="h-12 w-12 text-neutral-300 mx-auto mb-4" />
            <p className="text-neutral-500 text-lg font-medium">{t.noProducts}</p>
            <p className="text-neutral-400 text-sm mt-1">{t.tryDifferentSearch}</p>
          </div>
        )}

        {/* Empty category state */}
        {!hasProducts && !search && !isLoading && (
          <div className="text-center py-16">
            <p className="text-neutral-500 text-lg font-medium">{t.noProducts || 'No products found'}</p>
          </div>
        )}

        {/* Initial central spinner when no products are present yet */}
        {isLoading && !hasProducts && (
          <div className="flex justify-center py-20">
            <Grid
              height="80"
              width="80"
              color="#0ea5e9"
              ariaLabel="grid-loading"
              radius="12.5"
              visible={true}
            />
          </div>
        )}

        <div className={`grid grid-cols-2 gap-4 sm:gap-5 md:gap-6 lg:grid-cols-4 auto-rows-max transition-opacity duration-200 ${isLoading ? 'opacity-60' : 'opacity-100'}`}>
          {hasProducts && products.map((product) => {
            const hasMonthlyPrice = product.price != null && product.price > 0;
            const hasYearlyPrice = product.yearlyPrice != null && product.yearlyPrice > 0;
            
            let displayPrice = 0;
            let displayOriginalPrice = 0;
            let isYearlyBilling = false;

            if (hasMonthlyPrice) {
              displayOriginalPrice = product.price;
              displayPrice = (product.discountPrice != null && product.discountPrice > 0) ? product.discountPrice : product.price;
              isYearlyBilling = false;
            } else if (hasYearlyPrice) {
              displayOriginalPrice = product.yearlyPrice;
              displayPrice = (product.yearlyDiscountPrice != null && product.yearlyDiscountPrice > 0) ? product.yearlyDiscountPrice : product.yearlyPrice;
              isYearlyBilling = true;
            } else {
              displayPrice = product.discountPrice ?? product.price ?? 0;
              displayOriginalPrice = product.price ?? displayPrice;
            }

            const discountPercentage = displayOriginalPrice > displayPrice && displayOriginalPrice > 0
              ? Math.round(((displayOriginalPrice - displayPrice) / displayOriginalPrice) * 100)
              : 0;
            
            return (
              <Link
                key={product.id}
                to={`/product-detail/${product.id}`}
                onClick={() => dispatch(setSelectedProduct(product))}
                className="group relative bg-white rounded-xl shadow-sm hover:shadow-elegant transition-all duration-300 border border-blue-200 hover:border-blue-300 flex flex-col h-full hover:-translate-y-1"
              >
                {/* Image Container */}
                <div className="relative w-full overflow-hidden rounded-t-lg">
                  {/* Discount Badge - Top Right Corner */}
                  {discountPercentage > 0 && (
                    <div className="absolute top-0 right-0 z-20 bg-red-500 text-white text-[14px] font-bold px-2 py-0.5 rounded-bl-md shadow-md">
                      -{discountPercentage}%
                    </div>
                  )}
                  
                  <img
                    src={product.thumbnail}
                    alt={getMultilingualText(product.title, language)}
                    loading="lazy"
                    decoding="async"
                    className="w-full h-auto object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  
                  {/* Out of Stock Overlay */}
                  {product.stock <= 0 && (
                    <div className="absolute inset-0 bg-neutral-900/60 flex items-center justify-center backdrop-blur-sm">
                      <span className="text-white text-sm font-semibold px-4 py-2 bg-neutral-900 rounded-lg inline-flex items-center gap-2">
                        <XCircleIcon className="h-4 w-4" />
                        {t.outOfStock}
                      </span>
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col flex-grow gap-3">
                  {/* Title */}
                  <h3 className="text-sm font-semibold text-neutral-900 line-clamp-2 group-hover:text-brand-600 transition-colors leading-tight">
                    {getMultilingualText(product.title, language)}
                  </h3>

                  {/* Description */}
                  {product.description && (
                    <p className="text-xs text-neutral-500 line-clamp-1 leading-relaxed">
                      {getMultilingualText(product.description, language)}
                    </p>
                  )}

                  {/* Rating */}
                  <div className="flex items-center gap-2">
                    <div className="flex items-center gap-0.5">
                      {[0, 1, 2, 3, 4].map((star) => (
                        <StarIcon
                          key={star}
                          className={classNames(
                            star < Math.round(product.rating) ? 'text-brand-500' : 'text-gray-300',
                            'h-3.5 w-3.5 flex-shrink-0'
                          )}
                        />
                      ))}
                    </div>
                    <span className="text-xs font-semibold text-brand-600">
                      {product.rating}/5
                    </span>
                  </div>

                  {/* Price */}
                  <div className="flex items-baseline gap-2 pt-1">
                    <span className="text-lg font-bold text-neutral-900">
                      €{displayPrice}
                    </span>
                    {discountPercentage > 0 && (
                      <span className="text-xs text-neutral-400 line-through">
                        €{displayOriginalPrice}
                      </span>
                    )}
                  </div>
                  
                  {/* Billing Period */}
                  <div className="pt-1">
                    <span className="text-xs font-medium text-neutral-500">
                      Billed {isYearlyBilling ? 'yearly' : 'monthly'}
                    </span>
                  </div>

                  {/* Stock Status */}
                  <div className="pt-2">
                    {product.stock > 0 ? (
                      <span className="text-xs font-semibold text-emerald-600 inline-flex items-center gap-1.5">
                        <CheckCircleIcon className="h-3.5 w-3.5" />
                        In Stock
                      </span>
                    ) : (
                      <span className="text-xs font-semibold text-red-600 inline-flex items-center gap-1.5">
                        <XCircleIcon className="h-3.5 w-3.5" />
                        Unavailable
                      </span>
                    )}
                  </div>

                  {/* Button */}
                  <button
                    disabled={product.stock <= 0}
                    className={classNames(
                      "w-full py-2.5 px-3 rounded-lg font-semibold text-xs transition-all duration-200 flex items-center justify-center gap-2 mt-auto",
                      product.stock <= 0
                        ? 'bg-neutral-100 text-neutral-400 cursor-not-allowed'
                        : 'bg-brand-500 hover:bg-brand-600 text-white shadow-sm hover:shadow-md'
                    )}
                  >
                    {product.stock <= 0 ? (
                      <>
                        <LockClosedIcon className="h-4 w-4" />
                        Unavailable
                      </>
                    ) : (
                      <>
                        <EyeIcon className="h-4 w-4" />
                        View Details
                      </>
                    )}
                  </button>
                </div>
              </Link>
            );
          })}
        </div>
      </div>
    </div>
  );
}
